// WHAT: Context provider for global header state including search, notifications, and quick actions
// WHY:  Provides centralized state management for header functionality across the application
// DOMAIN: Shared → Contexts
// DEPENDENCIES: searchService from core services
// RISKS:
//   - NOTE: Search service integration may not handle all search types correctly
//   - NOTE: Notification management may have performance issues with many notifications
//   - NOTE: Quick actions state may not persist across navigation
//   - NOTE: Global progress state may conflict with component-level progress indicators

import type {
  Notification,
  SearchResult,
  QuickAction,
  HeaderState,
  HeaderContextType,
  HeaderProviderProperties,
} from "./HeaderContext.types";

import React, { createContext, useState, useCallback } from "react";

import { performSearch as searchService } from "@/domains/core/services/searchService";

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);
export { HeaderContext };

/**
 * WHAT:
 *   Context provider component that manages global header state including search,
 *   notifications, quick actions, and global progress.
 *
 * WHY:
 *   Centralizes header functionality state management and provides consistent
 *   interface for header features across the application.
 *
 * BEHAVIOR:
 *   - Manages search state and results
 *   - Manages notification list and unread count
 *   - Manages quick actions
 *   - Manages user menu state
 *   - Manages global progress indicator
 *   - Provides action handlers for all features
 *
 * RISKS:
 *   - NOTE: Search service integration may not handle all search types correctly
 *   - NOTE: Notification management may have performance issues with many notifications
 *   - NOTE: Quick actions state may not persist across navigation
 *   - NOTE: Global progress state may conflict with component-level progress indicators
 *
 * @param root0 - Props object
 *
 * @param root0.children - Child components
 *
 * @returns HeaderProvider component
 *
 */
export const HeaderProvider: React.FC<HeaderProviderProperties> = ({ children }) => {
  const [state, setState] = useState<HeaderState>({
    globalProgress: {
      isActive: false,
      message: "",
      progress: 0,
    },
    isNotificationPanelOpen: false,
    isSearchOpen: false,
    isUserMenuOpen: false,
    notifications: [],
    quickActions: [],
    searchQuery: "",
    searchResults: [],
    unreadCount: 0,
  });

  // NOTE: Search Actions
  const setSearchQuery = useCallback((query: string) => {
    setState((previous) => ({ ...previous, searchQuery: query }));
  }, []);

  const openSearch = useCallback(() => {
    setState((previous) => ({ ...previous, isSearchOpen: true }));
  }, []);

  const closeSearch = useCallback(() => {
    setState((previous) => ({
      ...previous,
      isSearchOpen: false,
      searchQuery: "",
      searchResults: [],
    }));
  }, []);

  /**
   * WHAT:
   *   Performs search using search service.
   *
   * WHY:
   *   Provides unified search functionality across the application.
   */
  const performSearch = useCallback(async (query: string): Promise<SearchResult[]> => {
    try {
      const searchFunction = searchService as (query: string) => Promise<SearchResult[]>;
      const results = await searchFunction(query);
      setState((previous) => ({ ...previous, searchResults: results }));
      return results;
    } catch {
      setState((previous) => ({ ...previous, searchResults: [] }));
      return [];
    }
  }, []);

  // NOTE: Notification Actions
  /**
   * WHAT:
   *   Adds new notification to list.
   *
   * WHY:
   *   Enables components to show notifications globally.
   */
  const addNotification = useCallback(
    (notification: Omit<Notification, "id" | "read" | "timestamp">) => {
      const newNotification: Notification = {
        ...notification,
        id: Date.now().toString(),
        read: false,
        timestamp: new Date(),
      };

      setState((previous) => ({
        ...previous,
        notifications: [newNotification, ...previous.notifications],
        unreadCount: previous.unreadCount + 1,
      }));
    },
    [],
  );

  const markAsRead = useCallback((id: string) => {
    setState((previous) => ({
      ...previous,
      notifications: previous.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
      unreadCount: Math.max(0, previous.unreadCount - 1),
    }));
  }, []);

  const markAllAsRead = useCallback(() => {
    setState((previous) => ({
      ...previous,
      notifications: previous.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }));
  }, []);

  const removeNotification = useCallback((id: string) => {
    setState((previous) => {
      const notification = previous.notifications.find((n) => n.id === id);
      return {
        ...previous,
        notifications: previous.notifications.filter((n) => n.id !== id),
        unreadCount: notification?.read
          ? previous.unreadCount
          : Math.max(0, previous.unreadCount - 1),
      };
    });
  }, []);

  const toggleNotificationPanel = useCallback(() => {
    setState((previous) => ({
      ...previous,
      isNotificationPanelOpen: !previous.isNotificationPanelOpen,
    }));
  }, []);

  // Quick Actions
  const addQuickAction = useCallback((action: QuickAction) => {
    setState((previous) => ({
      ...previous,
      quickActions: previous.quickActions.some((existing) => existing.id === action.id)
        ? previous.quickActions
        : [...previous.quickActions, action],
    }));
  }, []);

  const removeQuickAction = useCallback((id: string) => {
    setState((previous) => ({
      ...previous,
      quickActions: previous.quickActions.filter((a) => a.id !== id),
    }));
  }, []);

  const updateQuickAction = useCallback((id: string, updates: Partial<QuickAction>) => {
    setState((previous) => ({
      ...previous,
      quickActions: previous.quickActions.map((a) => (a.id === id ? { ...a, ...updates } : a)),
    }));
  }, []);

  // User Menu
  const toggleUserMenu = useCallback(() => {
    setState((previous) => ({ ...previous, isUserMenuOpen: !previous.isUserMenuOpen }));
  }, []);

  // Global Progress
  const setGlobalProgress = useCallback((progress: Partial<HeaderState["globalProgress"]>) => {
    setState((previous) => ({
      ...previous,
      globalProgress: { ...previous.globalProgress, ...progress },
    }));
  }, []);

  const value: HeaderContextType = {
    ...state,
    addNotification,
    addQuickAction,
    closeSearch,
    markAllAsRead,
    markAsRead,
    openSearch,
    performSearch,
    removeNotification,
    removeQuickAction,
    setGlobalProgress,
    setSearchQuery,
    toggleNotificationPanel,
    toggleUserMenu,
    updateQuickAction,
  };

  return <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>;
};
